// Species and methods. Catches store codes ("pike", "lure") so names show in whichever language is chosen.
import type { Catch, Kind } from "../shared/model.ts";
import { STRINGS } from "../shared/strings.ts";
import { fmtNum, speciesLabel, t } from "./i18n.ts";

export const SPECIES: Record<Kind, readonly string[]> = {
  fish: ["perch", "pike", "zander", "browntrout", "rainbowtrout", "grayling", "whitefish", "salmon", "char",
    "bream", "burbot", "ide", "roach", "vendace", "asp", "tench", "seatrout"],
  game: ["moose", "whitetail", "roedeer", "fallowdeer", "boar", "mountainhare", "brownhare", "fox", "raccoondog",
    "capercaillie", "blackgrouse", "hazelgrouse", "willowgrouse", "mallard", "teal", "goldeneye", "wigeon",
    "canadagoose", "greylag", "woodpigeon", "pheasant", "beaver", "woodcock"],
};
export const METHODS: Record<Kind, readonly string[]> = {
  fish: ["lure", "jig", "fly", "bait", "trolling", "ice", "net", "longline", "trapnet"],
  game: ["rifle", "shotgun", "bow", "trap"],
};
export const HUNT_STYLES = ["stand", "dog", "stalk", "drive", "waterfowl", "bow", "trapping"] as const;

/** The catch's species name in the current language (or as typed, for species the app doesn't know). */
export const speciesName = (c: Pick<Catch, "speciesId" | "species" | "kind">): string =>
  (c.speciesId && speciesLabel(c.speciesId)) || c.species || t(c.kind);

/** A known species' names in every language, lower case. */
export const speciesNames = (id: string): string[] =>
  Object.values(STRINGS).map((L) => (L.species as Record<string, string>)[id]?.toLowerCase()).filter((n): n is string => !!n);

/** Matches typed text to a known species in any language, so "Hauki" and "Pike" are the same fish. */
export function matchSpecies(text: string, kind: Kind): string | null {
  const q = text.trim().toLowerCase();
  if (!q) return null;
  for (const id of SPECIES[kind]) {
    for (const L of Object.values(STRINGS)) if ((L.species as Record<string, string>)[id]?.toLowerCase() === q) return id;
  }
  return null;
}

// Catches saved before translations stored English method names.
const LEGACY_METHOD: Record<string, string> = { Lure: "lure", Jig: "jig", Fly: "fly", Bait: "bait", Trolling: "trolling", "Ice fishing": "ice", Net: "net",
  Rifle: "rifle", Shotgun: "shotgun", Bow: "bow", Trap: "trap" };
/** Brings an older saved catch up to date (in place) and returns it. */
export function upgrade(c: Catch): Catch {
  if (c.method && LEGACY_METHOD[c.method]) c.method = LEGACY_METHOD[c.method];
  if (!c.speciesId && c.species) c.speciesId = matchSpecies(c.species, c.kind) ?? undefined;
  return c;
}

/** The species list for a combo box (combo.ts): your recent species first, then every known species of the kind in
 *  alphabetical order. Typing narrows it to names containing the text, in any language ("hauki" finds Pike). */
export function speciesOptions(kind: Kind, all: Catch[], locale: string, labels: { recent: string; all: string }) {
  return (query: string): { label?: string; items: string[] }[] => {
    const recent = [...new Set(all.filter((c) => c.kind === kind).sort((a, b) => b.time - a.time).map(speciesName))].slice(0, 5);
    const known = SPECIES[kind].map(speciesLabel).sort((a, b) => a.localeCompare(b, locale));
    if (!query) return [{ label: labels.recent, items: recent }, { label: recent.length ? labels.all : undefined, items: known }];
    const q = query.toLowerCase(), starts = (s: string) => (s.toLowerCase().startsWith(q) ? 0 : 1);
    const matchesKnown = SPECIES[kind].filter((id) => speciesNames(id).some((n) => n.includes(q))).map(speciesLabel);
    const matchesRecent = recent.filter((s) => s.toLowerCase().includes(q));
    return [{ items: [...new Set([...matchesRecent, ...matchesKnown])].sort((a, b) => starts(a) - starts(b)) }];
  };
}

/** "2,4 kg · 61 cm · Released" */
export function sizeLine(c: Catch): string {
  const parts: string[] = [];
  if (c.weightKg != null) parts.push(fmtNum(c.weightKg, 2) + " kg");
  if (c.lengthCm != null && c.kind === "fish") parts.push(fmtNum(c.lengthCm) + " cm");
  if (c.kind === "fish" && c.released) parts.push(t("released"));
  return parts.join(" · ");
}
